import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  Skeleton,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Download, FileUp, IdCard, Plus, Search, UserPlus, X } from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod'

import { ApiError, errorMessage } from '@/api/errors'
import {
  useCreateGymMember,
  useCurrentPermissions,
  useGymMemberExports,
  useGymMembers,
  useLocations,
} from '@/api/queries'
import type { GymMemberListItem, GymMemberStatus } from '@/api/types'
import { GYM_MEMBER_STATUSES } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'

/**
 * Anagrafica degli iscritti — ANA-06.
 *
 * ## Non è la pagina «Utenti», e la differenza è il punto
 *
 * `/utenti` sono gli **account** dell'app; `/operatori` sono le persone che
 * **lavorano** nella palestra. Qui ci sono i **clienti**, che nella maggioranza dei
 * casi non hanno un account e non lo avranno mai. Tre elenchi diversi con tre
 * significati diversi, e chiamarli tutti «membri» è ciò che questa pagina evita.
 *
 * ## Perché non è una tabella con il codice fiscale
 *
 * Perché il contract non lo manda: `GymMemberListItem` è uno schema **diverso** da
 * `GymMember`, deliberatamente privo di dati identificativi. Non è un permesso che
 * manca — è la scelta di non far uscire la rubrica della palestra da una pagina di
 * elenco. Il codice fiscale si vede aprendo una scheda.
 *
 * ## Perché non ci sono colonne ordinabili
 *
 * L'elenco è ordinato per **cognome** e non è configurabile: è l'ordine in cui si
 * cerca una persona a voce, ed è l'unico che un indice regge. Su una collezione a
 * cursore l'ordinamento lato client riguarda la sola pagina corrente, quindi non
 * sarebbe approssimativo: sarebbe falso.
 */
const searchSchema = z.object({
  q: z.string().optional(),
  status: z.enum(GYM_MEMBER_STATUSES).optional(),
  /** La sede sta nell'URL come gli altri filtri: un elenco filtrato si condivide e si ricarica. */
  locationId: z.string().uuid().optional(),
})

type GymMemberSearch = z.infer<typeof searchSchema>

export const Route = createFileRoute('/_app/iscritti/')({
  validateSearch: searchSchema,
  component: GymMembersPage,
})

const STATUS_LABELS: Record<GymMemberStatus, string> = {
  active: 'Attivo',
  suspended: 'Sospeso',
  ended: 'Cessato',
}

function GymMembersPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [creating, setCreating] = useState(false)

  const permissions = useCurrentPermissions()
  const exports = useGymMemberExports()
  // Nel dubbio si **nasconde**: mentre l'elenco delle appartenenze carica l'insieme è vuoto,
  // e un pulsante che compare per un istante e poi sparisce è peggio di uno che arriva tardi.
  const canExport = permissions.has('gym_member:export')

  /**
   * Lo scaricamento vero: si crea un'ancora, si clicca, si revoca l'URL.
   *
   * Non è un `window.open` sull'indirizzo dell'API: quella richiesta partirebbe **senza**
   * l'header di autorizzazione e senza quello dell'organizzazione, quindi tornerebbe `401` o
   * — peggio — il CSV di un'altra palestra. Il file arriva dal client HTTP come tutti gli
   * altri, e qui diventa un file.
   */
  async function downloadCsv(): Promise<void> {
    const { blob, filename } = await exports.csv.mutateAsync(
      search.status === undefined ? {} : { status: search.status },
    )
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    anchor.click()
    URL.revokeObjectURL(url)
  }

  const list = useGymMembers({
    query: search.q,
    status: search.status,
    locationId: search.locationId,
    limit: 50,
  })
  const rows = list.data?.data ?? []
  const hasFilters =
    search.q !== undefined || search.status !== undefined || search.locationId !== undefined

  /**
   * Le sedi, e il filtro **solo se ce n'è più di una**.
   *
   * Una palestra con una sede sola non ha niente da filtrare: quel selettore sarebbe una
   * casella con una voce, cioè superficie da mantenere su un'ipotesi. È la stessa ragione per
   * cui il selettore di organizzazione non compare a chi ha una sola appartenenza.
   */
  const locations = useLocations()
  const locationRows = locations.data ?? []
  const showLocationFilter = locationRows.length > 1

  function setFilter<K extends keyof GymMemberSearch>(key: K, value: GymMemberSearch[K]) {
    void navigate({ search: (prev) => ({ ...prev, [key]: value }) })
  }

  return (
    <>
      <PageHeader
        title="Iscritti"
        description="I clienti della palestra. Gli operatori stanno in «Operatori», gli account dell'app in «Utenti»."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/*
              Import ed export stanno accanto alla registrazione perché è lì che si va
              quando la palestra arriva da un altro gestionale: il primo gesto non è
              «registra un iscritto», è «portali dentro tutti».
            */}
            {canExport ? (
              <Button
                variant="outline"
                onClick={() => void downloadCsv()}
                loading={exports.csv.isPending}
              >
                <Download className="size-4" />
                Esporta CSV
              </Button>
            ) : null}
            <Button variant="outline" asChild>
              <Link to="/iscritti/import">
                <FileUp className="size-4" />
                Importa
              </Link>
            </Button>
            <Button onClick={() => setCreating(true)}>
              <UserPlus />
              Registra iscritto
            </Button>
          </div>
        }
      />

      <div
        className={cn(
          'mb-4 grid gap-2',
          showLocationFilter ? 'sm:grid-cols-[2fr_1fr_1fr]' : 'sm:grid-cols-[2fr_1fr]',
        )}
      >
        <Field label="Cerca" hint="Nome, cognome o numero di tessera.">
          {(props) => (
            <Input
              {...props}
              value={search.q ?? ''}
              placeholder="Rossi"
              onChange={(event) => setFilter('q', event.target.value || undefined)}
            />
          )}
        </Field>

        <Field label="Stato">
          {(props) => (
            <Select
              {...props}
              value={search.status ?? 'all'}
              onValueChange={(value) =>
                setFilter('status', value === 'all' ? undefined : (value as GymMemberStatus))
              }
              items={[
                { value: 'all', label: 'Ogni stato' },
                ...GYM_MEMBER_STATUSES.map((status) => ({
                  value: status,
                  label: STATUS_LABELS[status],
                })),
              ]}
            />
          )}
        </Field>

        {showLocationFilter ? (
          <Field
            label="Sede"
            hint="Chi non ha una sede di riferimento resta fuori: «nessuna» non è «tutte»."
          >
            {(props) => (
              <Select
                {...props}
                value={search.locationId ?? 'all'}
                onValueChange={(value) =>
                  setFilter('locationId', value === 'all' ? undefined : value)
                }
                items={[
                  { value: 'all', label: 'Ogni sede' },
                  ...locationRows.map((location) => ({
                    value: location.id,
                    label: location.name,
                  })),
                ]}
              />
            )}
          </Field>
        ) : null}
      </div>

      {hasFilters ? (
        <div className="mb-3">
          <Button variant="ghost" size="sm" onClick={() => void navigate({ search: {} })}>
            <X />
            Azzera filtri
          </Button>
        </div>
      ) : null}

      {list.isError && !list.data ? (
        <ErrorState
          message={errorMessage(list.error)}
          requestId={list.error instanceof ApiError ? list.error.requestId : undefined}
          onRetry={() => void list.refetch()}
        />
      ) : list.isPending ? (
        <ul className="flex flex-col gap-2">
          {[0, 1, 2, 3].map((i) => (
            <li key={i}>
              <Card radius="md" padding="sm">
                <Skeleton className="h-10 w-full" />
              </Card>
            </li>
          ))}
        </ul>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<IdCard />}
          title={hasFilters ? 'Nessun iscritto con questi filtri' : 'Nessun iscritto'}
          description={
            hasFilters
              ? 'Prova con mezzo cognome: la ricerca tollera gli errori di battitura.'
              : 'Registra la prima persona che si presenta al banco: bastano nome e cognome.'
          }
        />
      ) : (
        <div className={cn('transition-opacity', list.isFetching && 'opacity-60')}>
          <ul className="flex flex-col gap-2">
            {rows.map((member) => (
              <GymMemberRow key={member.id} member={member} />
            ))}
          </ul>
        </div>
      )}

      <Alert variant="neutral" className="mt-4" title="Che cosa mostra questo elenco">
        Nome, cognome, numero di tessera e stato — e nient'altro. Codice fiscale, nascita, residenza
        e contatti si vedono aprendo la scheda: una pagina di elenco non è il posto da cui estrarre
        la rubrica della palestra.
      </Alert>

      {creating ? <CreateGymMemberDialog onClose={() => setCreating(false)} /> : null}
    </>
  )
}

function GymMemberRow({ member }: { member: GymMemberListItem }) {
  const anonymized = member.anonymizedAt !== null
  return (
    <li>
      <Card radius="md" padding="sm" className="flex flex-wrap items-center gap-3">
        {/* La riga intera apre la scheda: al banco si tocca il nome, non un'icona a
          fine riga. È lì che stanno codice fiscale, consensi e tessera — cioè tutto
          ciò che l'elenco non mostra di proposito. */}
        <Link
          to="/iscritti/$gymMemberId"
          params={{ gymMemberId: member.id }}
          className="min-w-0 flex-1"
        >
          <p className="truncate font-medium">
            {member.lastName} {member.firstName}
          </p>
          <p className="text-muted-foreground text-xs">
            {member.memberNumber !== null ? `Tessera ${member.memberNumber} · ` : ''}
            {/* La sede solo quando c'è: «nessuna sede» in ogni riga di una palestra che ha
              una sede sola sarebbe rumore su ogni riga. */}
            {member.primaryLocationName !== null ? `${member.primaryLocationName} · ` : ''}
            Registrato il {formatDate(member.createdAt)}
          </p>
        </Link>
        {anonymized ? (
          <Badge variant="neutral">Anonimizzato</Badge>
        ) : (
          <Badge variant={member.status === 'active' ? 'success' : 'warning'}>
            {STATUS_LABELS[member.status]}
          </Badge>
        )}
      </Card>
    </li>
  )
}

/**
 * Registrazione al banco.
 *
 * **Nome e cognome sono gli unici campi obbligatori**, e il modulo non ne aggiunge:
 * pretendere il codice fiscale da chi si è appena presentato produce campi riempiti
 * con `x` per superare la validazione, che è peggio di un campo vuoto perché sembra
 * un dato.
 */
function CreateGymMemberDialog({ onClose }: { onClose: () => void }) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [taxCode, setTaxCode] = useState('')
  const [locationId, setLocationId] = useState('')
  const create = useCreateGymMember()

  // Come nel filtro: il campo compare solo se c'è più di una sede. Chiedere «quale sede» a
  // chi ne ha una sola è una domanda con una risposta sola.
  const locations = useLocations()
  const locationRows = locations.data ?? []
  const showLocation = locationRows.length > 1

  const canSubmit = firstName.trim() !== '' && lastName.trim() !== ''

  async function submit() {
    if (!canSubmit) return
    try {
      await create.mutateAsync({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim() === '' ? undefined : phone.trim(),
        // Il codice fiscale si manda in maiuscolo: la colonna accetta solo quello,
        // e correggerlo qui evita un `422` per un dato scritto bene ma minuscolo.
        taxCode: taxCode.trim() === '' ? undefined : taxCode.trim().toUpperCase(),
        ...(locationId === '' ? {} : { primaryLocationId: locationId }),
      })
      onClose()
    } catch {
      // Il messaggio lo mostra il blocco qui sotto: `mutateAsync` rilancia, e
      // ingoiare l'errore qui chiuderebbe la finestra su una registrazione fallita.
    }
  }

  return (
    <div className="bg-background/80 fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
      <div className="border-border bg-card w-full max-w-lg rounded-lg border p-4 shadow-lg">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Registra iscritto</h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Chiudi">
            <X />
          </Button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Nome">
            {(props) => (
              <Input
                {...props}
                value={firstName}
                autoFocus
                onChange={(event) => setFirstName(event.target.value)}
              />
            )}
          </Field>
          <Field label="Cognome">
            {(props) => (
              <Input
                {...props}
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            )}
          </Field>
          <Field label="Telefono" hint="Facoltativo.">
            {(props) => (
              <Input {...props} value={phone} onChange={(event) => setPhone(event.target.value)} />
            )}
          </Field>
          <Field label="Codice fiscale" hint="Facoltativo: arriva con il documento.">
            {(props) => (
              <Input
                {...props}
                value={taxCode}
                className="font-mono uppercase"
                onChange={(event) => setTaxCode(event.target.value)}
              />
            )}
          </Field>
          {showLocation ? (
            <Field
              label="Sede di riferimento"
              className="sm:col-span-2"
              hint="Serve a sapere di chi è: non decide dove può entrare."
            >
              {(props) => (
                <Select
                  {...props}
                  value={locationId}
                  onValueChange={setLocationId}
                  items={[
                    { value: '', label: 'Nessuna' },
                    ...locationRows.map((location) => ({
                      value: location.id,
                      label: location.name,
                    })),
                  ]}
                />
              )}
            </Field>
          ) : null}
        </div>

        {create.isError ? (
          <Alert variant="danger" className="mt-3">
            {errorMessage(create.error)}
          </Alert>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Annulla
          </Button>
          <Button disabled={!canSubmit} loading={create.isPending} onClick={() => void submit()}>
            <Plus />
            Registra
          </Button>
        </div>

        <p className="text-muted-foreground mt-3 text-xs">
          <Search className="mr-1 inline size-3" />
          Bastano nome e cognome: il resto si completa quando arriva il documento.
        </p>
      </div>
    </div>
  )
}
